// Bericht eines Workflows (/workflows/:id/bericht, Runde 10): Eingeschrieben, Aktiv, Fertig, Ziel erreicht, Abgebrochen, Fehler,
// Quoten, je Schritt (erreicht, ausgeführt, übersprungen, Fehler, wartet davor, Ziel danach) und der Verlauf über die Tage.
// Jede Zahl ist ein Knopf: ein Klick zeigt darunter die Läufe dahinter (mit denselben Aktionen wie auf der Läufe-Seite).
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fehlerText } from "../api";
import { workflowsApi, type WorkflowLauf } from "../api/workflows";
import { useAuth } from "../auth/AuthContext";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { useSeitenKopf } from "../daten/seite";
import { datumText } from "../werkzeuge/zeit";
import { useLaden } from "./daten";
import { WORKFLOW_STATUS, schrittInfo } from "./hilfen";
import { LaeufeTabelle, type LaufAktion } from "./LaeufeTabelle";
import { dauerLang, prozent, zielInfo } from "./teil2Hilfen";
import { workflows2Api, type Bericht, type BerichtLaeufeFilter, type LaufGruppe, type SchrittKlasse } from "./teil2Api";
import { ZeitraumWahl, useZeitraum } from "./ZeitraumWahl";
import "./workflows2.css";

interface Auswahl {
  titel: string;
  filter: BerichtLaeufeFilter;
}

function Zahl({ n, onClick, aktiv, titel }: { n: number; onClick: () => void; aktiv?: boolean; titel?: string }) {
  return (
    <button type="button" className={`wf-zahlknopf${aktiv ? " wf-zahlknopf--an" : ""}`} disabled={n === 0} onClick={onClick} title={n ? (titel ?? "Läufe dazu anzeigen") : undefined}>
      {n}
    </button>
  );
}

function Kachel({ label, wert, hinweis, onClick, aktiv, ton }: { label: string; wert: string | number; hinweis?: string; onClick?: () => void; aktiv?: boolean; ton?: "gut" | "schlecht" }) {
  const inhalt = (
    <>
      <span className="wf-kachel__wert">{wert}</span>
      <span className="wf-kachel__label">{label}</span>
      {hinweis ? <span className="wf-kachel__hinweis leise">{hinweis}</span> : null}
    </>
  );
  const klasse = `wf-kachel${ton ? ` wf-kachel--${ton}` : ""}${aktiv ? " wf-kachel--an" : ""}`;
  return onClick ? (
    <button type="button" className={klasse} onClick={onClick} disabled={wert === 0}>
      {inhalt}
    </button>
  ) : (
    <div className={klasse}>{inhalt}</div>
  );
}

function Verlauf({ bericht }: { bericht: Bericht }) {
  const reihe = bericht.verlauf;
  if (!reihe.length) return <p className="leise wf-leer">In diesem Zeitraum wurde niemand eingeschrieben.</p>;
  const hoechst = Math.max(1, ...reihe.map((t) => Math.max(t.eingeschrieben, t.ziel, t.beendet)));
  const woche = bericht.aufloesung === "woche";
  return (
    <div className="wf-verlauf-grafik" role="img" aria-label={`Verlauf: eingeschrieben und Ziel erreicht je ${woche ? "Woche" : "Tag"}`}>
      <div className="wf-saeulen">
        {reihe.map((t) => (
          <div key={t.tag} className="wf-saeule" title={`${woche ? "Woche ab " : ""}${datumText(t.tag)}: ${t.eingeschrieben} eingeschrieben, ${t.ziel} Ziel erreicht, ${t.beendet} beendet`}>
            <span className="wf-saeule__balken wf-saeule__balken--ein" style={{ height: `${(t.eingeschrieben / hoechst) * 100}%` }} />
            <span className="wf-saeule__balken wf-saeule__balken--ziel" style={{ height: `${(t.ziel / hoechst) * 100}%` }} />
          </div>
        ))}
      </div>
      <div className="wf-verlauf-achse leise">
        <span>{datumText(reihe[0].tag)}</span>
        <span>
          <span className="wf-legende wf-legende--ein" /> eingeschrieben <span className="wf-legende wf-legende--ziel" /> Ziel erreicht
        </span>
        <span>{datumText(reihe[reihe.length - 1].tag)}</span>
      </div>
    </div>
  );
}

function Laeufe({ workflowId, auswahl, kannVerwalten, onSchliessen, onGeaendert }: { workflowId: string; auswahl: Auswahl; kannVerwalten: boolean; onSchliessen: () => void; onGeaendert: () => void }) {
  const meldung = useMeldung();
  const g = useLaden(() => workflows2Api.berichtLaeufe(workflowId, { ...auswahl.filter, limit: 50 }), [workflowId, JSON.stringify(auswahl.filter)]);
  const [mehr, setMehr] = useState<WorkflowLauf[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const aktion = async (l: { id: string }, a: LaufAktion) => {
    try {
      await workflowsApi.laufAktion(l.id, a);
      await g.neuLaden();
      onGeaendert();
    } catch (e) {
      meldung.fehler(e, "Lauf nicht geändert");
    }
  };
  const weitere = async () => {
    const c = cursor === undefined ? g.daten?.cursor : cursor;
    if (!c) return;
    try {
      const r = await workflows2Api.berichtLaeufe(workflowId, { ...auswahl.filter, limit: 50, cursor: c });
      setMehr((m) => [...m, ...r.data]);
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      meldung.fehler(e, "Nicht geladen");
    }
  };
  const ersteIds = new Set((g.daten?.data ?? []).map((l) => l.id));
  const laeufe = [...(g.daten?.data ?? []), ...mehr.filter((l) => !ersteIds.has(l.id))];
  const hatMehr = cursor === undefined ? !!g.daten?.has_more : !!cursor;
  return (
    <section className="wf-abschnitt wf-drill" aria-label={auswahl.titel}>
      <div className="wf-abschnitt__kopf">
        <h2>{auswahl.titel}</h2>
        <span className="leise wf-abschnitt__hinweis">{g.daten ? `${g.daten.total ?? laeufe.length} ${g.daten.total === 1 ? "Lauf" : "Läufe"}` : ""}</span>
        <span className="wf-abschnitt__rechts">
          <button type="button" className="textknopf" onClick={onSchliessen}>
            Schließen
          </button>
        </span>
      </div>
      {g.fehler && !g.daten ? <FehlerBox fehler={g.fehler} onNochmal={() => void g.neuLaden()} /> : null}
      {g.laedt && !g.daten ? <Laden klein /> : laeufe.length ? <LaeufeTabelle laeufe={laeufe} zeigeWorkflow={false} kannVerwalten={kannVerwalten} onAktion={aktion} /> : <p className="leise wf-leer">Keine Läufe.</p>}
      {hatMehr ? (
        <div className="wf-mehr">
          <button type="button" className="knopf" onClick={() => void weitere()}>
            Weitere laden
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default function BerichtSeite() {
  const { workflowId = "" } = useParams();
  const { nutzer } = useAuth();
  const kannVerwalten = !!nutzer?.permissions.includes("workflows_verwalten");
  const [zeitraum, setZeitraum] = useZeitraum(30);
  const g = useLaden(() => workflows2Api.bericht(workflowId, zeitraum.von, zeitraum.bis), [workflowId, zeitraum.von, zeitraum.bis], 20000);
  const [auswahl, setAuswahl] = useState<Auswahl | null>(null);
  const b = g.daten;
  useSeitenKopf([{ label: "Workflows", to: "/workflows" }, { label: b?.name ?? "…", to: `/workflows/${encodeURIComponent(workflowId)}` }, { label: "Bericht" }]);

  const zeige = (titel: string, filter: BerichtLaeufeFilter) => setAuswahl({ titel, filter: { von: zeitraum.von, bis: zeitraum.bis, ...filter } });
  const gruppe = (g2: LaufGruppe, titel: string) => () => zeige(titel, { gruppe: g2 });
  const istGruppe = (g2: LaufGruppe) => auswahl?.filter.gruppe === g2 && !auswahl.filter.schritt_id;
  const schritt = (id: string, klasse: SchrittKlasse, titel: string) => () => zeige(titel, { schritt_id: id, klasse });

  if (g.fehler && !b) return <div className="wf-seite wf-seite--breit"><FehlerBox fehler={g.fehler} onNochmal={() => void g.neuLaden()} /></div>;
  if (!b) return <div className="wf-seite wf-seite--breit"><Laden /></div>;

  const z = b.gesamt;
  const hatZiel = b.hat_ziele || z.ziel > 0;

  return (
    <div className="wf-seite wf-seite--breit wf-bericht">
      <div className="wf-kopf">
        <Link to={`/workflows/${encodeURIComponent(b.workflow_id)}`} className="wf-kopf__zurueck" title="Zum Workflow">
          <Icon name="chevron-links" groesse={14} />
          {b.name}
        </Link>
        <h1 className="wf-bericht__titel">Bericht</h1>
        <span className={`wf-status wf-status--${b.status}`}>{WORKFLOW_STATUS[b.status as keyof typeof WORKFLOW_STATUS] ?? b.status}</span>
        <span className="leise">Fassung {b.version}</span>
        <span className="wf-kopf__rechts">
          <Link className="knopf" to={`/workflows/laeufe?workflow_id=${encodeURIComponent(b.workflow_id)}`}>
            Alle Läufe
          </Link>
          <Link className="knopf" to="/workflows/berichte">
            Alle Berichte
          </Link>
        </span>
      </div>

      <div className="wf-bericht__leiste">
        <ZeitraumWahl zeitraum={zeitraum} onChange={(zr) => { setAuswahl(null); setZeitraum(zr); }} />
      </div>
      {b.gekappt ? <div className="wf-meldung wf-meldung--warnung"><Icon name="warnung" groesse={15} /><span>Der Zeitraum hat mehr als 20.000 Läufe, der Bericht zählt nur die ersten. Bitte den Zeitraum verkleinern.</span></div> : null}

      <div className="wf-kacheln" role="group" aria-label="Läufe nach Stand">
        <Kachel label="Eingeschrieben" wert={z.eingeschrieben} onClick={() => zeige("Alle eingeschriebenen Läufe", {})} aktiv={!!auswahl && !auswahl.filter.gruppe && !auswahl.filter.schritt_id && auswahl.filter.ziel_nach === undefined} />
        <Kachel label="Aktiv" wert={z.aktiv} hinweis="wartet oder läuft" onClick={gruppe("aktiv", "Aktive Läufe")} aktiv={istGruppe("aktiv")} />
        {z.pausiert ? <Kachel label="Pausiert" wert={z.pausiert} onClick={gruppe("pausiert", "Pausierte Läufe")} aktiv={istGruppe("pausiert")} /> : null}
        <Kachel label="Fertig" wert={z.fertig} hinweis="alle Schritte durch" onClick={gruppe("fertig", "Fertige Läufe")} aktiv={istGruppe("fertig")} />
        {hatZiel ? <Kachel label="Ziel erreicht" wert={z.ziel} ton="gut" onClick={gruppe("ziel", "Läufe mit erreichtem Ziel")} aktiv={istGruppe("ziel")} /> : null}
        <Kachel label="Abgebrochen" wert={z.abgebrochen} hinweis="von Hand, gefiltert, Lead weg" onClick={gruppe("abgebrochen", "Abgebrochene Läufe")} aktiv={istGruppe("abgebrochen")} />
        <Kachel label="Fehler" wert={z.fehler} ton={z.fehler ? "schlecht" : undefined} onClick={gruppe("fehler", "Läufe mit Fehler")} aktiv={istGruppe("fehler")} />
      </div>

      <div className="wf-kacheln wf-kacheln--klein" role="group" aria-label="Quoten und Zeiten">
        {hatZiel ? <Kachel label="Ziel erreicht" wert={prozent(z.ziel_quote)} hinweis="von allen Eingeschriebenen" /> : null}
        <Kachel label="Abgeschlossen" wert={prozent(z.abschluss_quote)} hinweis="fertig oder Ziel erreicht" />
        <Kachel label="Fehlerquote" wert={prozent(z.fehler_quote)} />
        <Kachel label="Ø Dauer bis fertig" wert={dauerLang(z.mittlere_dauer_s)} hinweis="Läufe ohne Ziel" />
        {hatZiel ? <Kachel label="Ø Zeit bis Ziel" wert={dauerLang(z.mittlere_zeit_bis_ziel_s)} /> : null}
        <Kachel label="Entwürfe und Aufgaben" wert={`${z.mail_entwuerfe} · ${z.sms_entwuerfe} · ${z.aufgaben}`} hinweis="Mails · SMS · Aufgaben, gesendet wird nichts" />
      </div>

      <section className="wf-abschnitt">
        <div className="wf-abschnitt__kopf">
          <h2>Je Schritt</h2>
          <span className="leise wf-abschnitt__hinweis">Stand der Schritte heute</span>
        </div>
        {b.schritte.length === 0 ? (
          <p className="leise wf-leer">Der Workflow hat keinen Schritt.</p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle wf-bericht__tabelle">
              <thead>
                <tr>
                  <th>Schritt</th>
                  <th className="wf-zahl">Erreicht</th>
                  <th className="wf-bericht__balken-kopf" aria-label="Anteil an den Eingeschriebenen" />
                  <th className="wf-zahl" title="Der Schritt lief">Ausgeführt</th>
                  <th className="wf-zahl" title="Der Schritt hat nichts getan; bei einem Filter: der Lead wurde ausgefiltert">Übersprungen</th>
                  <th className="wf-zahl">Fehler</th>
                  <th className="wf-zahl" title="Der Lauf steht noch vor diesem Schritt (Wartezeit, Sendefenster, Pause)">Wartet davor</th>
                  {hatZiel ? <th className="wf-zahl" title="Das Ziel wurde erreicht, nachdem dieser Schritt zuletzt gelaufen war">Ziel danach</th> : null}
                </tr>
              </thead>
              <tbody>
                {b.schritte.map((s) => {
                  const name = s.name || schrittInfo(s.art).label;
                  const t = `Schritt ${s.nummer} (${name})`;
                  return (
                    <tr key={s.id} className="wf-bericht__zeile">
                      <td>
                        <span className="wf-bericht__nr">{s.nummer}</span> {name}
                      </td>
                      <td className="wf-zahl"><Zahl n={s.erreicht} onClick={schritt(s.id, "erreicht", `${t}: erreicht`)} /></td>
                      <td className="wf-bericht__balken-zelle">
                        <span className="wf-balken" title={prozent(s.erreicht_quote)}>
                          <span className="wf-balken__fuell" style={{ width: `${Math.min(100, s.erreicht_quote ?? 0)}%` }} />
                        </span>
                        <span className="leise wf-balken__text">{prozent(s.erreicht_quote)}</span>
                      </td>
                      <td className="wf-zahl"><Zahl n={s.ok} onClick={schritt(s.id, "ok", `${t}: ausgeführt`)} /></td>
                      <td className="wf-zahl"><Zahl n={s.uebersprungen} onClick={schritt(s.id, "uebersprungen", `${t}: übersprungen`)} /></td>
                      <td className="wf-zahl"><Zahl n={s.fehler} onClick={schritt(s.id, "fehler", `${t}: Fehler`)} /></td>
                      <td className="wf-zahl"><Zahl n={s.wartet} onClick={schritt(s.id, "wartet", `${t}: Lauf steht davor`)} /></td>
                      {hatZiel ? <td className="wf-zahl"><Zahl n={s.ziel_danach} onClick={() => zeige(`${t}: Ziel danach erreicht`, { ziel_nach: s.id })} /></td> : null}
                    </tr>
                  );
                })}
                {hatZiel ? (
                  <tr className="wf-bericht__zeile wf-bericht__zeile--leise">
                    <td className="leise">Ziel schon vor dem ersten Schritt</td>
                    <td colSpan={6} />
                    <td className="wf-zahl"><Zahl n={b.ziel_vor_dem_ersten_schritt} onClick={() => zeige("Ziel vor dem ersten Schritt erreicht", { ziel_nach: "_start" })} /></td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {b.ziele.length ? (
        <section className="wf-abschnitt">
          <div className="wf-abschnitt__kopf">
            <h2>Ziele</h2>
            <span className="leise wf-abschnitt__hinweis">welches Ziel den Lauf beendet hat</span>
          </div>
          <div className="tabelle-rahmen">
            <table className="tabelle wf-bericht__tabelle">
              <thead>
                <tr>
                  <th>Ziel</th>
                  <th>Art</th>
                  <th className="wf-zahl">Erreicht</th>
                  <th className="wf-zahl">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {b.ziele.map((zl) => (
                  <tr key={zl.id}>
                    <td>{zl.name}</td>
                    <td className="leise">{zielInfo(zl.art).label}</td>
                    <td className="wf-zahl">{zl.erreicht}</td>
                    <td className="wf-zahl leise">{prozent(z.eingeschrieben ? Math.round((zl.erreicht * 1000) / z.eingeschrieben) / 10 : null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="wf-abschnitt">
        <div className="wf-abschnitt__kopf">
          <h2>Verlauf</h2>
          <span className="leise wf-abschnitt__hinweis">{b.aufloesung === "woche" ? "je Woche" : "je Tag"}, {datumText(b.von)} bis {datumText(b.bis)}</span>
        </div>
        <Verlauf bericht={b} />
      </section>

      {auswahl ? <Laeufe key={JSON.stringify(auswahl.filter)} workflowId={workflowId} auswahl={auswahl} kannVerwalten={kannVerwalten} onSchliessen={() => setAuswahl(null)} onGeaendert={() => void g.neuLaden()} /> : null}
      <p className="leise wf-fuss">{g.fehler ? fehlerText(g.fehler) : null}</p>
    </div>
  );
}
